// AI Generated Component
import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonModalService } from 'app/modules/modals/services/mon-modal/mon-modal.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PageService } from '@client/app/services/entities/page/page.service';
import { PageAccessibilityRepoCheckErrorsUpdateType } from '@client/app/services/api/page-accessibility/page-accessibility-repo.service';

@Component({
    selector: 'page-details-section-accessibility-information',
    standalone: false,
    template: `
        <div class="page-details-information-site">
            <div class="pt-4 px-3 d-flex">
                <div class="px-2">
                    <mon-table-policy-type
                        *ngIf="check.result_type"
                        class="inline-block"
                        [color]="!check.ignored ? iconColor(check.issue_abbr) : 'grey'"
                        [isMobileCheck]="check.mobile"
                        [passed]="check.errors_count === 0 && !check.ignored"
                        [target]="check.result_type">
                    </mon-table-policy-type>
                </div>
                <div [innerHTML]="check.name"></div>
            </div>
            <div class="pt-1 pb-3 pl-12 pr-3 border-bottom">
                <acc-help-center-btn [check]="check"></acc-help-center-btn>
            </div>
            <div class="pl-3 pr-1 tabs-and-buttons-container">
                <page-details-sections-tabs [tab]="tab" [options]="tabOptions" (onTabChange)="onTabChange($event.tab)">
                </page-details-sections-tabs>
                <div class="buttons-container pb-1">
                    <button *ngIf="tab === 'pending' && !check.ignored && (allowIgnoreAction || check.result_type === 'review')"
                            [attr.aria-label]="(check.result_type === 'review' ? 'Review all issues' : 'Ignore all issues') | translate"
                            class="btn btn-border-radius btn-secondary outer-t-smurf"
                            (click)="onClickIgnore()">
                        {{ (check.result_type === 'review' ? 'Review all issues' : 'Ignore all issues') | translate }}
                    </button>
                    <button *ngIf="tab !== 'pending'"
                            [attr.aria-label]="(check.result_type === 'review' ? 'Remove review for all issues' : 'Unignore all issues') | translate"
                            class="btn btn-border-radius btn-secondary outer-t-smurf"
                            (click)="onClickUnignore()">
                        {{ (check.result_type === 'review' ? 'Remove review for all issues' : 'Unignore all issues') | translate }}
                    </button>
                    <button *ngIf="tab !== 'pending' && check.ignored && !check.ignored_on_page"
                            [attr.aria-label]="'Check is ignored for the entire domain' | translate"
                            class="btn btn-border-radius btn-secondary outer-t-smurf"
                            [disabled]="true"
                            translate>
                        Check is ignored for the entire domain
                    </button>
                    <button data-test="ignore-check-button"
                            (click)="checkStatus(true)"
                            [attr.aria-label]="'Ignore check' | translate"
                            class="btn btn-border-radius btn-secondary outer-t-smurf"
                            *ngIf="allowIgnoreAction && !check.ignored"
                            translate>
                        Ignore check
                    </button>
                    <button (click)="checkStatus(false)"
                            data-test="unignore-check-button"
                            [attr.aria-label]="'Unignore check' | translate"
                            class="btn btn-border-radius btn-secondary outer-t-smurf"
                            *ngIf="allowIgnoreAction && check.ignored && check.ignored_on_page"
                            translate>
                        Unignore check
                    </button>
                    <button data-test="mark-check-fixed-button"
                            (click)="confirmMarkCheckAsFixed()"
                            [attr.aria-label]="'Mark check as fixed' | translate"
                            class="btn btn-border-radius btn-secondary outer-t-smurf"
                            translate>
                        Mark check as fixed
                    </button>
                </div>
            </div>

            @if (tab === 'pending') {
                <page-details-section-accessibility-information-content-component
                        type="pending"
                        [check]="check"
                        (updateError)="reviewError($event.error, $event.value, $event.comment)"
                        (refresh)="refreshAfterFasttrack()"
                        [page]="page"
                />
            }
            @if (tab === 'review') {
                <page-details-section-accessibility-information-content-component
                        type="review"
                        [check]="check"
                        (updateError)="reviewError($event.error, $event.value, $event.comment)"
                        (refresh)="refreshAfterFasttrack()"
                        [page]="page"
                />
            }
        </div>
    `,
})
export class PageDetailsSectionAccessibilityInformationComponent implements OnInit, OnChanges {
    @Input() check: any;
    @Input() page: any;
    @Output() refreshErrors = new EventEmitter<{ update: boolean }>();
    @Output() refreshPage = new EventEmitter<void>();

    tab: string = 'pending';
    tabOptions: any[] = [];
    allowIgnoreAction: boolean = false;

    constructor (
        private translateService: TranslateService,
        private monModalService: MonModalService,
        private sessionService: SessionService,
        private pageService: PageService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnChanges (changes: SimpleChanges): void {
        if (changes['check'] && this.check) {
            this.updateTabOptions();
        }
    }

    activate (): void {
        this.tab = 'pending';
        this.allowIgnoreAction = !this.sessionService.isDomainGroup();
        this.updateTabOptions();
    }

    updateTabOptions (): void {
        const tabOptions = [
            {
                name: this.translateService.getString('Pending'),
                value: 'pending',
            },
        ];

        if (this.check) {
            if (this.check.result_type === 'review' && !this.check.ignored) {
                tabOptions.push({
                    name: this.translateService.getString('Reviewed'),
                    value: 'review',
                });
            } else {
                if (this.allowIgnoreAction) {
                    tabOptions.push({
                        name: this.translateService.getString('Ignored'),
                        value: 'review',
                    });
                }
            }
        }

        this.tabOptions = tabOptions;
    }

    iconColor (abbr: string): string {
        const colors = {
            'wcag-a': abbr === 'A',
            'wcag-aa': abbr === 'AA',
            'wcag-aaa': abbr === 'AAA',
        };
        let result = 'link';

        for (const i in colors) {
            if (colors[i] === true) {
                result = i;
                break;
            }
        }

        return result;
    }

    onTabChange (tab: string): void {
        this.tab = tab;
    }

    reviewAllErrors (value: boolean, comment?: string): void {
        const params: any = {
            type: 'errors',
            ignored_reviewed: value,
            id: this.check.id,
        };
        if (comment) {
            params.comment = comment;
        }

        this.pageService.updateAccessibilityCheckErrors(this.page.id, params).then(
            () => {
                this.refreshErrors.emit({ update: true });
                this.refreshPage.emit();
            },
            () => {}, // noop equivalent
        );
    }

    reviewError (error: any, value: boolean, comment?: string): void {
        error.ignored_reviewed = value;
        if (comment) {
            error.comment = comment;
        }

        this.updateError(error).then(
            () => {
                this.refreshErrors.emit({ update: true });
                const errorCount = this.check.errors.filter((err: any) => err.ignored);
                if (this.check.errors.length - (errorCount.length + 1) <= 0) {
                    this.refreshPage.emit();
                }
            },
            () => {}, // noop equivalent
        );
    }

    checkStatus (status: boolean): void {
        let confirmText: string;

        if (status) {
            confirmText = ' <strong>' + this.translateService.getString('Are you sure you want to ignore this check for this page?') + '</strong>';
        } else {
            confirmText = ' <strong>' + this.translateService.getString('Are you sure you want to unignore this check for this page?') + '</strong>';
        }

        confirmText += '<br /><br />' + this.translateService.getString('Add reason (Optional)');

        const params: any = {
            type: 'check',
            ignored: status,
        };

        this.monModalService.prompt(confirmText, {}).then(
            (comment: string) => {
                if (comment) {
                    params.comment = comment;
                }

                this.pageService.updateAccessibilityCheck(this.page.id, this.check.id, params).then(
                    () => {
                        this.refreshErrors.emit({ update: true });
                        this.refreshPage.emit();
                        this.check.ignored = !this.check.ignored;
                    },
                    () => {}, // noop equivalent
                );
            },
            () => {}, // noop equivalent
        );
    }

    updateError (error: any) {
        const errorUpdate: PageAccessibilityRepoCheckErrorsUpdateType = {
            id: error.id,
            ignored_reviewed: error.ignored_reviewed,
            type: error.type,
            comment: error.comment,
        };
        return this.pageService.updateAccessibilityError(this.page.id, errorUpdate);
    }

    onClickIgnore (): void {
        let text = this.translateService.getString('Are you sure you want to ignore all errors for this check?');
        if (this.check.result_type === 'review') {
            text = this.translateService.getString('Are you sure you want to review all errors for this check?');
        }
        text += '<br /><br />' + this.translateService.getString('Add reason (Optional)');

        this.monModalService.prompt(text, {}).then(
            (comment: string) => {
                this.reviewAllErrors(true, comment);
            },
            () => {}, // noop equivalent
        );
    }

    onClickUnignore (): void {
        let text = this.translateService.getString('Are you sure you want to unignore all errors for this check?');
        if (this.check.result_type === 'review') {
            text = this.translateService.getString('Are you sure you want to remove review on all errors for this check?');
        }
        text += '<br /><br />' + this.translateService.getString('Add reason (Optional)');

        this.monModalService.prompt(text, {}).then(
            (comment: string) => {
                this.reviewAllErrors(false, comment);
            },
            () => {}, // noop equivalent
        );
    }

    refreshAfterFasttrack (): void {
        this.refreshErrors.emit({ update: true });
        this.refreshPage.emit();
    }

    markCheckAsFixed (): void {
        this.pageService.deleteAccessibilityCheck(this.page.id, this.check.id).then(
            () => {
                this.refreshErrors.emit({ update: true });
                this.refreshPage.emit();
            },
            () => {}, // noop equivalent
        );
    }

    confirmMarkCheckAsFixed (): void {
        const confirmText = this.translateService.getString('Are you sure you want to mark this check as fixed?');
        this.monModalService.confirm(confirmText, {}).then(
            () => {
                this.markCheckAsFixed();
            },
            () => {}, // noop equivalent
        );
    }
}
